import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, FormControlLabel, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { Client } from '../http';
import {
  CouponOption, couponOptionLabel, selectableCoupons,
} from '../features/plans/planCoupon';

export interface PlanPromoCodeValue {
  promo_code: string;
  apply_promo_code: boolean;
}

export interface PlanPromoCodeFieldProps {
  promoCode: string;
  applyPromoCode: boolean;
  promoCost: string;
  onChange: (value: PlanPromoCodeValue) => void;
}

/**
 * The plan's promo code: unlike the plan coupon it is applied only when the
 * customer brings it, in the signup URL or typed at checkout. The signup
 * card shows the price with it, and the code to enter. Switching off keeps
 * the code, as for the plan coupon.
 */
const PlanPromoCodeField = ({
  promoCode, applyPromoCode, promoCost, onChange,
}: PlanPromoCodeFieldProps) => {
  const [coupons, setCoupons] = useState<CouponOption[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Client.get<CouponOption[]>('/api/v1/admin/coupons')
      .then((res) => { if (!cancelled) setCoupons(res.data ?? []); })
      .catch((e: unknown) => { if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, []);

  // Promo codes are dollar amounts off; the code the plan holds stays listed.
  const options = useMemo(
    () => selectableCoupons(coupons.filter((c) => (c.dollar_amount ?? 0) > 0 || c.promo_code === promoCode), promoCode),
    [coupons, promoCode],
  );
  const selected = options.find((c) => c.promo_code === promoCode) ?? null;

  return (
    <Box>
      <Typography variant="subtitle2">Promo code</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Applied only when the customer brings it, in the signup link or typed at
        checkout, on top of the plan coupon. The signup card shows the price with
        it in green, and the code to enter.
      </Typography>
      <Stack spacing={1.5}>
        <FormControlLabel
          labelPlacement="start"
          sx={{ justifyContent: 'space-between', ml: 0, mr: 0 }}
          control={(
            <Switch
              checked={applyPromoCode}
              onChange={(e) => onChange({ apply_promo_code: e.target.checked, promo_code: promoCode })}
              inputProps={{ 'aria-label': 'Apply a promo code' }}
            />
          )}
          label={<Typography variant="body2">Apply a promo code</Typography>}
        />
        <Autocomplete
          options={options}
          value={selected}
          disabled={!applyPromoCode}
          getOptionLabel={couponOptionLabel}
          isOptionEqualToValue={(a, b) => a.promo_code === b.promo_code}
          onChange={(_, value) => onChange({ apply_promo_code: applyPromoCode, promo_code: value?.promo_code ?? '' })}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Promo code"
              helperText={loadError
                ? `Could not load the coupons: ${loadError}`
                : 'Dollar amount coupons from the coupons table. The promo comes off before the plan coupon.'}
              error={!!loadError || (applyPromoCode && !promoCode)}
            />
          )}
        />
        {applyPromoCode && (
          <TextField
            label="Price with the promo code ($ per month)"
            value={promoCost}
            disabled
            helperText="Worked out from the price shown on the signup site and the promo code."
          />
        )}
      </Stack>
    </Box>
  );
};

export default PlanPromoCodeField;
